import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { buildProductOptions } from '../../Components/PriceSimulation/productOptions';
import { buildSkuInstancesMap } from '../../Components/PriceSimulation/skuInstances';
import { toDisplayPackage } from '../../Components/PriceSimulation/packageStructureTransformer';
import type { DisplayPackage, FilterOption, RootState } from '../../Components/PriceSimulation/PriceSimulation.types';
import { transformPackageToWorksheet } from '../../Components/PriceSimulation/worksheetDataTransformer';
import type { PackageWorksheet } from '../../Components/PriceSimulation/worksheetDataTransformer';
import { PriceSimulationStatus } from '../../Constants/PriceSimulation/priceSimulationConstants';
import { VossProductTypeOptions } from '../../Constants/VOSS/vossProductType';
import { firstNonEmptyString } from '../../Utils/stringUtils';
import useDynamicPlanOptions from './useDynamicPlanOptions';

const DEFAULT_BILLING_CYCLE = 12;
const NO_WORKSHEETS: PackageWorksheet[] = [];

const sortByName = <T extends { name?: string | null }>(options: T[]): T[] =>
    [...options].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'sv', { numeric: true, sensitivity: 'base' }));

export const pruneToOptions = (selected: FilterOption[], options: FilterOption[]): FilterOption[] => {
    if (options.length === 0) return selected;
    const validIds = new Set(options.map((o) => o.id));
    const pruned = selected.filter((s) => validIds.has(s.id));
    return pruned.length === selected.length ? selected : pruned;
};

const usePruneToOptions = (options: FilterOption[], selected: FilterOption[], setSelected: (next: FilterOption[]) => void) => {
    useEffect(() => {
        const pruned = pruneToOptions(selected, options);
        if (pruned !== selected) setSelected(pruned);
    }, [options, selected, setSelected]);
};

const usePriceSimulationFilters = () => {
    const { t } = useTranslation();
    const packages = useSelector((state: RootState) => state.PriceSimulationReducer.packages);
    const packageDetailsMap = useSelector((state: RootState) => state.PriceSimulationReducer.packageDetailsMap);

    const [selectedPackages, setSelectedPackages] = useState<FilterOption[]>([]);
    const [selectedTiers, setSelectedTiers] = useState<FilterOption[]>([]);
    const [selectedPlans, setSelectedPlans] = useState<FilterOption[]>([]);
    const [selectedProductTypes, setSelectedProductTypes] = useState<FilterOption[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<FilterOption[]>([]);
    const [modificationStatuses, setModificationStatuses] = useState<FilterOption[]>([]);
    const [billingCycle, setBillingCycle] = useState(DEFAULT_BILLING_CYCLE);
    const [onlyPlansWithVolume, setOnlyPlansWithVolume] = useState(false);

    const packageOptions = useMemo<FilterOption[]>(
        () => sortByName((packages ?? []).map((pkg) => ({ id: pkg.id ?? '', name: firstNonEmptyString(pkg.name, `Package ${pkg.id}`) }))),
        [packages]
    );

    const tierOptions = useMemo<FilterOption[]>(() => {
        const tierMap = new Map<string, FilterOption>();
        selectedPackages.forEach((pkg) => {
            (packageDetailsMap[pkg.id]?.tiers ?? []).forEach(({ tierId, name }) => {
                if (tierId !== undefined && !tierMap.has(tierId)) {
                    tierMap.set(tierId, { id: tierId, name });
                }
            });
        });
        return sortByName(Array.from(tierMap.values()));
    }, [selectedPackages, packageDetailsMap]);

    const planOptions = useDynamicPlanOptions(selectedPackages.length > 0 ? selectedPackages : packageOptions);

    const productTypeOptions = useMemo(() => sortByName(VossProductTypeOptions.map((opt) => ({ ...opt, name: t(opt.nameKey) }))), [t]);

    const modificationStatusOptions = useMemo(() => PriceSimulationStatus.map((s) => ({ ...s, name: t(s.nameKey) })), [t]);

    const selectedPackageIds = useMemo(() => selectedPackages.map((pkg) => String(pkg.id)), [selectedPackages]);

    const worksheetPackages = useMemo<PackageWorksheet[]>(
        () =>
            selectedPackages
                .map((pkg) => (pkg.id in packageDetailsMap ? transformPackageToWorksheet(packageDetailsMap[pkg.id], String(pkg.id), selectedTiers) : null))
                .filter((p) => p !== null),
        [selectedPackages, packageDetailsMap, selectedTiers]
    );

    const displayPackages = useMemo<DisplayPackage[]>(
        () =>
            selectedPackages
                .map((pkg) => (pkg.id in packageDetailsMap ? toDisplayPackage(packageDetailsMap[pkg.id], selectedTiers, billingCycle) : null))
                .filter((p): p is DisplayPackage => p !== null),
        [selectedPackages, packageDetailsMap, selectedTiers, billingCycle]
    );

    const allPackageWorksheets = useMemo<PackageWorksheet[]>(
        () =>
            selectedPackages.length > 0
                ? NO_WORKSHEETS
                : packageOptions
                      .filter((pkg) => pkg.id in packageDetailsMap)
                      .map((pkg) => transformPackageToWorksheet(packageDetailsMap[pkg.id], String(pkg.id), []))
                      .filter((worksheet) => worksheet !== null),
        [selectedPackages, packageOptions, packageDetailsMap]
    );

    // With packages selected, product options follow the tier filter.
    // Keyed by SKU so a product spanning several tiers is listed once.
    const productSourcePackages = selectedPackages.length > 0 ? worksheetPackages : allPackageWorksheets;

    const skuInstancesMap = useMemo(() => buildSkuInstancesMap(packageDetailsMap), [packageDetailsMap]);

    const productOptions = useMemo<FilterOption[]>(() => {
        const productMap = new Map<string, { sku: string; name: string }>();
        productSourcePackages.forEach((pkg) => {
            pkg.tiers.forEach((tier) => {
                tier.productGroups.forEach((group) => {
                    group.products.forEach((product) => {
                        const sku = product.productSku;
                        if (sku && !productMap.has(sku)) {
                            productMap.set(sku, { sku, name: firstNonEmptyString(product.name, sku) });
                        }
                    });
                });
            });
        });
        return sortByName(buildProductOptions(Array.from(productMap.values()), skuInstancesMap));
    }, [productSourcePackages, skuInstancesMap]);

    usePruneToOptions(planOptions, selectedPlans, setSelectedPlans);
    // Prune tier picks that no longer exist once a package is removed (e.g. a tier unique to the removed
    // package). Without this the stale tier empties worksheetPackages/productOptions, and the length-guarded
    // product prune below can't tell that from "still loading" — leaving a stale product whitelist that hides
    // every remaining product. Guarded on length so a transient empty (loading) doesn't wipe valid picks.
    usePruneToOptions(tierOptions, selectedTiers, setSelectedTiers);
    usePruneToOptions(productOptions, selectedProducts, setSelectedProducts);

    const labelledSelectedProducts = useMemo(() => {
        const optionsById = new Map(productOptions.map((option) => [option.id, option]));
        return selectedProducts.map((product) => optionsById.get(product.id) ?? product);
    }, [productOptions, selectedProducts]);

    // Deselecting the last package leaves the tier options empty, so the length-guarded prune can't tell
    // "no package" from "still loading" and would keep stale chips.
    useEffect(() => {
        if (selectedPackages.length > 0) return;
        setSelectedTiers((prev) => (prev.length > 0 ? [] : prev));
    }, [selectedPackages]);

    return {
        selectedPackages,
        setSelectedPackages,
        selectedTiers,
        setSelectedTiers,
        selectedPlans,
        setSelectedPlans,
        selectedProductTypes,
        setSelectedProductTypes,
        selectedProducts: labelledSelectedProducts,
        setSelectedProducts,
        modificationStatuses,
        setModificationStatuses,
        billingCycle,
        setBillingCycle,
        packageOptions,
        tierOptions,
        planOptions,
        productTypeOptions,
        productOptions,
        skuInstancesMap,
        modificationStatusOptions,
        selectedPackageIds,
        worksheetPackages,
        allPackageWorksheets,
        displayPackages,
        onlyPlansWithVolume,
        setOnlyPlansWithVolume,
    };
};

export default usePriceSimulationFilters;
